import type { JSX } from "react";
import { Image, Link } from "@/components";
import type { CardNewsProps } from "./types";

/**
 * @name CardNews
 * @description
 * Organism-level component that renders a news/article card.
 * Displays an image, category badge, date, and title.
 *
 * @param {CardNewsProps} props - The properties for the component.
 * @param {string} props.title - The title of the news article.
 * @param {string} props.category - The category of the news.
 * @param {string} props.date - The date of the news.
 * @param {string} [props.image] - The image URL.
 * @param {string} [props.href] - The link URL.
 *
 * @returns {JSX.Element} The rendered CardNews component.
 *
 * @example
 * <CardNews
 *   title="Nueva política de vacaciones"
 *   category="Recursos Humanos"
 *   date="12 de Enero, 2024"
 *   image="/path/to/image.jpg"
 *   href="/noticias/123"
 * />
 */
const CardNews = ({
  title,
  category,
  date,
  image,
  href = "#"
}: CardNewsProps): JSX.Element => (
  <Link
    replace
    href={href}
    className="group block h-full bg-card text-card-foreground rounded-2xl overflow-hidden shadow-md hover:shadow-xl border border-border flex flex-col transition-all"
  >
    <div className="relative h-48 w-full overflow-hidden bg-secondary flex items-center justify-start p-8 group-hover:bg-muted transition-colors">
      <div className="absolute top-4 right-4 z-10 bg-primary text-primary-foreground text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
        {category}
      </div>
      <div className="relative w-24 h-24">
        <Image
          src={image}
          alt={category}
          width={96}
          height={96}
          className="w-full h-full object-contain drop-shadow-md transform group-hover:scale-110 transition-transform duration-500"
        />
      </div>
    </div>
    <div className="p-5 flex flex-col flex-grow">
      <span className="text-xs text-muted-foreground mb-2 block font-medium">
        {date}
      </span>
      <h3 className="text-lg font-bold leading-tight mb-2 line-clamp-3 group-hover:text-primary transition-colors">
        {title}
      </h3>
    </div>
  </Link>
);

export default CardNews;
